/**
 * Team page content.
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Our Team Of Experts — Professional Team Introduction Page (SEO Optimized
 * 2026)" requirement document (MBA_MERN_Solutions/Leadership Team WEB.docx),
 * which replaces the earlier "team introduction OVER ALL.docx". Do not reword,
 * shorten or extend this copy — update the document first.
 *
 * What this file adds on top of the document is presentation data only: the
 * `id` anchors, the photographs already held by the project, `initials` for
 * the two leaders with no photograph, and `icon` keys into the Home page's
 * existing registry (../Home/homeIcons).
 */

export const teamSeo = {
  title:
    "Meet Our Expert Team | ISO Consultants, Compliance & Business Growth Specialists | MBA Solutions",
  description:
    "Meet MBA Solutions professional team of ISO consultants, certification experts, technology specialists and business development professionals delivering global compliance and sustainability solutions.",
  /* the document's URL Slug */
  slug: "/our-team",
  keywords: [
    // Primary Keywords
    "MBA Solutions Team",
    "ISO Certification Consultants",
    "Compliance Experts",
    "Management System Consultants",
    "Certification Professionals",
    // Secondary Keywords
    "ISO Lead Auditor Experts",
    "Business Development Consultant",
    "Food Safety Consultants",
    "Sustainability Consultants",
    "ISO Implementation Specialists",
  ].join(", "),
};

export const heroContent = {
  h1: "Meet Our Team Of Experts",
  subtitle:
    "Experienced Professionals Helping Businesses Achieve Global Standards",
  paragraphs: [
    "At MBA Solutions, our strength comes from our people — a team of dedicated professionals who combine technical expertise, industry knowledge and business experience to deliver effective certification and compliance solutions.",
    "Our consultants, auditors, technology specialists and business development professionals work together to help organizations improve their management systems, achieve international certifications and build sustainable business practices.",
    "With expertise across ISO standards, food safety, sustainability, technology and business consulting, our team provides complete support from initial consultation to successful certification implementation.",
  ],
};

/* ---- Our Leadership Team ------------------------------------------------
   The four leaders, in the document's own order. */

const TEAM_ASSETS = `${process.env.PUBLIC_URL}/assets/Team`;

export const leadershipContent = {
  title: "Our Leadership Team",
  members: [
    {
      id: "muhammad-atif",
      name: "Muhammad Atif",
      designation: "Founder & Chief Executive Officer",
      /* the project's existing CEO portrait (also used on the About page) and
         his existing introduction video */
      image: `${TEAM_ASSETS}/Team1.webp`,
      video: `${TEAM_ASSETS}/atif.mp4`,
      paragraphs: [
        "Muhammad Atif is the Founder and Chief Executive Officer of MBA Solutions, leading the organization with a strong vision for certification excellence, compliance improvement and sustainable business growth.",
        "With more than 13 years of local and international experience, he has successfully guided organizations in developing and implementing internationally recognized management systems.",
      ],
      expertiseIntro: "His expertise covers:",
      expertise: [
        {
          title: "Management System Expertise",
          items: [
            "ISO 9001 Quality Management System",
            "ISO 14001 Environmental Management System",
            "ISO 45001 Occupational Health & Safety",
            "ISO 22000 Food Safety Management System",
            "FSSC 22000",
            "BRCGS Food Safety",
          ],
        },
        {
          title: "Technical & Compliance Expertise",
          items: [
            "ISO 17020 Inspection Systems",
            "ISO 17025 Laboratory Management Systems",
            "Management System Audits",
            "Sustainability Assessments",
            "EcoVadis Improvement Support",
          ],
        },
      ],
      closing:
        "His leadership focuses on delivering practical solutions that help organizations improve performance, strengthen compliance and achieve long-term success.",
    },
    {
      id: "muhammad-abubakr",
      name: "Muhammad Abubakr",
      designation: "Chief Technology Officer",
      image: null, // no photograph in the project or the document
      initials: "MA",
      paragraphs: [
        "Muhammad Abubakr is a technology and information security professional with over 20 years of international experience in technology management, compliance and digital systems.",
        "He supports organizations in strengthening their technology infrastructure and information security practices.",
      ],
      expertise: [
        {
          title: "Areas Of Expertise:",
          items: [
            "ISO 27001 Information Security Management",
            "GDPR Compliance",
            "ITIL Framework",
            "Cybersecurity Practices",
            "Risk Management",
            "Enterprise Network Solutions",
            "System Administration",
            "Technology Consulting",
          ],
        },
      ],
      closing:
        "His expertise helps businesses create secure, efficient and reliable technology environments aligned with international requirements.",
    },
    {
      id: "zara-atif",
      name: "Ms. Zara Atif",
      designation: "Head of Marketing & Business Development",
      /* her existing photograph from the team API, stored with the site */
      image: `${TEAM_ASSETS}/zara-atif.webp`,
      paragraphs: [
        "Ms. Zara Atif manages marketing strategy, client engagement and business development initiatives at MBA Solutions.",
        "She plays a key role in strengthening company relationships, identifying market opportunities and developing strategic partnerships.",
      ],
      expertise: [
        {
          title: "Professional Expertise:",
          items: [
            "Marketing Strategy Development",
            "Business Growth Planning",
            "Client Relationship Management",
            "Market Research",
            "Partnership Development",
            "Customer Communication",
          ],
        },
      ],
      closing:
        "Her focus is building meaningful business relationships and connecting organizations with the right certification and consulting solutions.",
    },
    {
      id: "samana-batool",
      name: "Samana Batool",
      designation: "Business Development Manager (BDM)",
      image: null, // no photograph in the project or the document
      initials: "SB",
      paragraphs: [
        "Samana Batool is a results-driven Business Development Manager with 5 years of professional experience in business development, client relationship management and consultancy services.",
        "She specializes in understanding client requirements, identifying business opportunities and supporting organizations throughout their consultancy journey.",
        "With a strong understanding of customer needs and business growth strategies, she contributes to expanding partnerships and delivering successful client experiences.",
      ],
      /* the document's "Professional Skills & Expertise" block, which follows
         her profile directly and completes it */
      skillsTitle: "Professional Skills & Expertise",
      skills: [
        {
          title: "Business Development & Growth",
          items: [
            "Lead Generation Strategies",
            "Business Opportunity Identification",
            "Client Acquisition",
            "Market Research",
            "Sales Planning",
            "Partnership Development",
          ],
        },
        {
          title: "Client Relationship Management",
          items: [
            "Client Communication",
            "Requirement Understanding",
            "Customer Support",
            "Proposal Coordination",
            "Relationship Building",
            "Long-Term Client Management",
          ],
        },
        {
          title: "Consultancy Support",
          items: [
            "Certification Requirement Analysis",
            "Client Project Coordination",
            "Compliance Solution Understanding",
            "Service Coordination",
            "Business Consultation Support",
          ],
        },
        {
          title: "Professional Strengths",
          items: [
            "Strong communication skills",
            "Strategic thinking",
            "Problem-solving approach",
            "Result-oriented mindset",
            "Team collaboration",
            "Client-focused attitude",
          ],
        },
      ],
    },
  ],
};

/* ---- Our Combined Expertise --------------------------------------------- */

export const combinedExpertiseContent = {
  title: "Our Combined Expertise",
  subtitle: "A Multi-Disciplinary Team Supporting Global Business Needs",
  intro: "MBA Solutions team provides expertise in:",
  areas: [
    {
      icon: "certificate",
      title: "ISO & Management Systems",
      to: "/iso-certification-services",
      itemsLabel: "Supporting organizations with:",
      items: [
        "ISO Certification",
        "Implementation Support",
        "Internal Audits",
        "Management System Improvement",
      ],
    },
    {
      icon: "food",
      title: "Food Safety & Quality",
      to: "/food-safety-services",
      itemsLabel: "Supporting:",
      items: ["HACCP", "ISO 22000", "FSSC 22000", "Halal Certification", "GMP/GHP"],
    },
    {
      icon: "textile",
      title: "Textile & Sustainability Compliance",
      to: "/textile-compliance-services",
      itemsLabel: "Supporting:",
      items: ["GOTS", "GRS", "WRAP", "Sedex / SMETA", "ESG Requirements"],
    },
    {
      icon: "supplyChain",
      title: "Supply Chain & Responsible Business",
      to: "/supply-chain-certification-services",
      itemsLabel: "Supporting:",
      items: ["FSC Chain of Custody", "PEFC", "Sustainability Practices"],
    },
  ],
};

/* ---- Our Consulting Philosophy ------------------------------------------ */

export const philosophyContent = {
  title: "Our Consulting Philosophy",
  subtitle: "Expertise. Partnership. Results.",
  lead: "We believe successful certification is achieved through collaboration, practical implementation and continuous improvement.",
  intro: "Our team focuses on:",
  stages: [
    {
      icon: "consultation",
      title: "Understanding",
      description: "Learning your business processes and challenges.",
    },
    {
      icon: "documentation",
      title: "Planning",
      description: "Creating customized solutions based on your requirements.",
    },
    {
      icon: "implementation",
      title: "Implementation",
      description: "Supporting your team in applying international standards.",
    },
    {
      icon: "improvement",
      title: "Improvement",
      description: "Helping your organization achieve sustainable growth.",
    },
  ],
};

/* ---- Why Choose MBA Solutions Team? ------------------------------------- */

export const whyChooseContent = {
  title: "Why Choose MBA Solutions Team?",
  subtitle: "Professionals Who Understand Business Challenges",
  intro: "Organizations choose MBA Solutions because we provide:",
  reasons: [
    "Experienced consultants",
    "International standards knowledge",
    "Industry-specific expertise",
    "Practical implementation support",
    "Professional project management",
    "Long-term partnership approach",
  ],
  outro:
    "Our team does not only help businesses achieve certifications — we help them build stronger systems for future growth.",
};

/* ---- Final CTA ----------------------------------------------------------- */

export const finalCtaContent = {
  title: "Work With Our Expert Team",
  paragraph:
    "Partner with MBA Solutions for professional certification, compliance and sustainability solutions.",
  /* Both buttons are the document's own. "Meet Our Experts" has no target
     given, so it scrolls to this page's leadership team; only the
     consultation button leaves the page. */
  cta: { label: "Get Your Consultation Today", to: "/contact" },
  secondaryCta: { label: "Meet Our Experts", href: "#leadership-team" },
};
